import React, { useEffect, useRef, useState } from 'react';
import './Modal.css';

/**
 * 共通モーダルコンポーネント
 * 
 * 機能:
 * - ドラッグ&ドロップでモーダルを移動
 * - ESCキーで閉じる
 * - タイトルバーに×ボタン
 * - ライト/ダークモード対応
 * 
 * @param {Object} props
 * @param {string} props.title - モーダルのタイトル
 * @param {function} props.onClose - 閉じるときのコールバック
 * @param {React.ReactNode} props.children - モーダルのコンテンツ
 * @param {string} props.width - モーダルの横幅（デフォルト: '500px'）
 * @param {string} props.maxWidth - モーダルの最大幅（デフォルト: '90%'）
 * @param {boolean} props.isDraggable - ドラッグ可能かどうか（デフォルト: true）
 * @param {string} props.className - 追加のクラス名
 */
const Modal = ({ 
  title, 
  onClose, 
  children, 
  width = '500px',
  maxWidth = '90%',
  isDraggable = true,
  className = ''
}) => {
  const modalRef = useRef(null);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });

  // ESCキーで閉じる
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [onClose]);

  // ドラッグ開始
  const handleMouseDown = (e) => {
    if (!isDraggable) return;
    
    // タイトルバー以外でのドラッグは無効
    if (!e.target.closest('.modal-title-bar')) return;
    
    // ×ボタンのクリックは除外
    if (e.target.closest('.modal-close-button')) return;

    setIsDragging(true);
    setDragStart({
      x: e.clientX - position.x,
      y: e.clientY - position.y
    });
  };

  // ドラッグ中
  useEffect(() => {
    const handleMouseMove = (e) => {
      if (!isDragging) return;

      const newX = e.clientX - dragStart.x;
      const newY = e.clientY - dragStart.y;

      setPosition({ x: newX, y: newY });
    };

    const handleMouseUp = () => {
      setIsDragging(false);
    };

    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging, dragStart]);

  // モーダルのスタイル
  const modalStyle = {
    width,
    maxWidth,
    transform: `translate(${position.x}px, ${position.y}px)`,
    cursor: isDragging ? 'grabbing' : 'default'
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div 
        ref={modalRef}
        className={`modal-content ${className}`}
        style={modalStyle}
        onClick={(e) => e.stopPropagation()}
      >
        {/* タイトルバー */}
        <div 
          className={`modal-title-bar ${isDraggable ? 'draggable' : ''}`}
          onMouseDown={handleMouseDown}
        >
          <h3>{title}</h3>
          <button 
            className="modal-close-button" 
            onClick={onClose}
            title="閉じる (ESC)"
          >
            ×
          </button>
        </div>

        {/* コンテンツ */}
        <div className="modal-body">
          {children}
        </div>
      </div>
    </div>
  );
};

export default Modal;
